import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DocumentPreparationForm } from "./document/DocumentPreparationForm";
import { useParams } from "react-router-dom";
import { MISSING_ID_IN_ROUTE, ROLE, STATUS } from "@/utils/constant";
import { useAuth } from "@/features/auth/useAuth";
import { hasCommonElement } from "@/utils/helper/hasCommonElement";
import {
  useDownloadOPMZipByRFS,
  useGenerateHPTARXlsx,
  useGeneratePoleDbXlsx,
  useRFS,
  useSyncRfsStatus,
} from "@/api/rfs";
import { DocumentReviewForm } from "./document/DocumentReviewForm";
import { DocumentRejectForm } from "./document/DocumentRejectForm";
import { P } from "@/components/ui/typography";
import { Loading } from "@/components/ui/loading";
import { DocumentGenerateHPDBForm } from "./document/DocumentGenerateHPDBForm";
import { DownloadButton, SyncButton } from "@/components/ui/button";
import { useOPMFDTs } from "@/api/opm-fdt";
import { OPMFATData, useInfiniteOPMFATs } from "@/api/opm-fat";
import { useNNEvidences } from "@/api/nn-evidence";
import {
  useHPTARGeneralMessage,
  useHPTARInvalids,
  useHPTARs,
  useInfiniteKmzInvalids,
} from "@/api/hptar";
import {
  isHPTARValidated,
  isNNEvidenceValidated,
} from "./document/utils/validation";
import { useGenerateABDFDTs } from "@/api/generate-abd-fdt";
import { useGenerateABDFATsCount } from "@/api/generate-abd-fat";
import { MediaDownloadTarget, useDownloadMediaUriFilename } from "@/api/media";
import { useSupportingDocumentByRfsId } from "@/api/supporting-document";
import { canDownload } from "../opm/utils/canDownload";
import { useEffect, useState } from "react";
import RegenerateSection from "./RegenerateSection";

export function StatusPanel() {
  const { id } = useParams<{ id: string }>();
  if (!id) {
    throw new Error(MISSING_ID_IN_ROUTE);
  }
  const [opmFatDataRef, setOpmFatDataRef] = useState<OPMFATData[] | undefined>(
    [],
  );

  const { data: rfs, isPending } = useRFS({ id });

  const auth = useAuth();
  const { roles } = auth.getUser();
  const canSync = hasCommonElement(roles, [
    ROLE.ADMINISTRATOR,
    ROLE.VENDOR,
    ROLE.VALIDATOR_DOCUMENT_OFFICER,
  ]);
  const isSyncableStatus =
    rfs?.statusRfsSubmission === STATUS.RFS.PREPARATION_DATA;
  const { data: opmFdts } = useOPMFDTs({ rfsId: id });
  const {
    data: opmFats,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteOPMFATs({
    opmFdtRfsID: id,
  });
  const { data: abdFdts } = useGenerateABDFDTs({ rfsId: id });
  const { data: abdFatCount } = useGenerateABDFATsCount({ rfsId: id });

  const { data: nnEvidences } = useNNEvidences({ rfsId: id });
  const nnEvidence = nnEvidences?.[0];

  const { data: hptars } = useHPTARs({ rfsId: id });
  const { data: hptarInvalids } = useHPTARInvalids({ rfsId: id });
  const { data: generalMessages } = useHPTARGeneralMessage({
    rfsId: id,
  });
  const { data: kmzInvalids } = useInfiniteKmzInvalids({ rfsId: id });

  const { data: supportingDocument } = useSupportingDocumentByRfsId({
    rfsId: id,
  });

  useEffect(() => {
    if (opmFats?.pages) {
      const allContent = opmFats.pages.flatMap((page) => page.content);
      setOpmFatDataRef(allContent);
    }
  }, [opmFats]);

  useEffect(() => {
    if (!hasNextPage) return;

    fetchNextPage();
  }, [hasNextPage, fetchNextPage]);

  const isDownloadableOPM = canDownload(
    opmFdts,
    opmFatDataRef,
    abdFdts,
    abdFatCount,
  );
  const isValidNNEvidence = isNNEvidenceValidated({ nnEvidences });
  const isValidHPTAR = isHPTARValidated({
    hptars,
    hptarInvalids,
    hptarGeneralMessages: generalMessages,
    kmzInvalids: kmzInvalids?.pages?.[0]?.content,
  });
  const isReviseAbdKmz = supportingDocument?.abdDariKmzIsRevise;
  const isReviseAbdDwg = supportingDocument?.abdDariDwgIsRevise;
  const isReviseDwg = supportingDocument?.abdDwgIsRevise;
  const isReviseEvidence = supportingDocument?.evidenceFeederIsRevise;
  const isReviseBakp = supportingDocument?.bakpIsRevise;
  const isReviseCmld = supportingDocument?.cmldIsRevise;
  const isReviseClampHook = supportingDocument?.clampHookIsRevise;
  const isReviseEvidenceOlt = supportingDocument?.evidenceOltIsRevise;

  // Download
  const { mutate: downloadMediaUri } = useDownloadMediaUriFilename();
  const { mutate: downloadOPMZip, isPending: isDownloadingOPMZip } =
    useDownloadOPMZipByRFS();
  const { mutate: downloadPoleDb, isPending: isDownloadingPoleDb } =
    useGeneratePoleDbXlsx();
  const { mutate: downloadHPTAR, isPending: isDownloadingHPTAR } =
    useGenerateHPTARXlsx();

  // Sync
  const { mutate: syncRfsStatus, isPending: isSyncingRfsStatus } =
    useSyncRfsStatus();

  async function downloadMediaSequentially(target: MediaDownloadTarget[]) {
    for (const media of target) {
      downloadMediaUri(media);
      await new Promise((resolve) => setTimeout(resolve, 3000));
    }
  }

  async function handleDownloadAll() {
    if (!id) {
      throw new Error(MISSING_ID_IN_ROUTE);
    }

    const mediaPaths: MediaDownloadTarget[] = [
      {
        path: nnEvidence?.documentNnEvidencePath,
        fileName: nnEvidence?.documentNnEvidenceName,
      },
      {
        path: supportingDocument?.abdDariKmzPath,
        fileName: supportingDocument?.abdDariKmzName,
      },
      {
        path: supportingDocument?.abdDariDwgPath,
        fileName: supportingDocument?.abdDariDwgName,
      },
      {
        path: supportingDocument?.abdDwgPath,
        fileName: supportingDocument?.abdDwgName,
      },
      {
        path: supportingDocument?.evidenceFeederPath,
        fileName: supportingDocument?.evidenceFeederName,
      },
      {
        path: supportingDocument?.bakpPath,
        fileName: supportingDocument?.bakpName,
      },
      {
        path: supportingDocument?.cmldPath,
        fileName: supportingDocument?.cmldName,
      },
      {
        path: supportingDocument?.clampHookPath,
        fileName: supportingDocument?.clampHookName,
      },
      {
        path: supportingDocument?.evidenceOltPath,
        fileName: supportingDocument?.evidenceOltName,
      },
    ].filter(
      (target): target is MediaDownloadTarget =>
        target.path != null && target.fileName != null,
    );

    downloadOPMZip({ id, name: `ZIP-${rfs?.clusterId}.zip` });
    downloadPoleDb({ id, name: `POLE-${rfs?.clusterId}.xlsx` });
    downloadHPTAR({ id, name: `HPTAR-${rfs?.clusterId}.xlsx` });
    downloadMediaSequentially(mediaPaths);
  }

  function handleSync() {
    if (!id) {
      throw new Error(MISSING_ID_IN_ROUTE);
    }
    syncRfsStatus(id);
  }

  if (isPending) {
    return <Loading />;
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col justify-between gap-2 lg:flex-row lg:items-center">
          <TitleStatus status={rfs?.statusRfsSubmission} />

          <div className="flex gap-x-1">
            <RegenerateSection />
            {canSync && isSyncableStatus && (
              <SyncButton onClick={handleSync} loading={isSyncingRfsStatus}>
                Synchronize
              </SyncButton>
            )}
            {rfs?.statusRfsSubmission !== STATUS.RFS.GENERATING_HPDB && (
              <DownloadButton
                disabled={
                  !isDownloadableOPM &&
                  !isValidNNEvidence &&
                  !isValidHPTAR &&
                  isReviseAbdKmz &&
                  isReviseAbdDwg &&
                  isReviseDwg &&
                  isReviseEvidence &&
                  isReviseBakp &&
                  isReviseCmld &&
                  isReviseClampHook &&
                  isReviseEvidenceOlt
                }
                onClick={handleDownloadAll}
                loading={
                  isDownloadingOPMZip ||
                  isDownloadingPoleDb ||
                  isDownloadingHPTAR
                }
              >
                Download All
              </DownloadButton>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-12">
        <DocumentForm status={rfs?.statusRfsSubmission} />
      </CardContent>
    </Card>
  );
}

function DocumentForm({ status }: { status?: string }) {
  switch (status) {
    case STATUS.RFS.PREPARATION_DATA:
      return <DocumentPreparationForm />;
    case STATUS.RFS.REVISION_BY_LN:
      return <DocumentReviewForm />;
    case STATUS.RFS.REJECTED_DOCUMENT_RFS:
      return <DocumentRejectForm />;
    case STATUS.RFS.GENERATING_HPDB:
      return <DocumentGenerateHPDBForm />;
    default:
      return <P>This RFS has an unrecognizable status.</P>;
  }
}

function TitleStatus({ status }: { status?: string }) {
  switch (status) {
    case STATUS.RFS.PREPARATION_DATA:
      return <CardTitle>RFS Process (Task Vendor)</CardTitle>;
    case STATUS.RFS.REVISION_BY_LN:
      return <CardTitle>RFS Process (Task Validator)</CardTitle>;
    case STATUS.RFS.REJECTED_DOCUMENT_RFS:
      return <CardTitle>RFS Process (Task Vendor)</CardTitle>;
    case STATUS.RFS.GENERATING_HPDB:
      return null;
    default:
      return <CardTitle>Status</CardTitle>;
  }
}
